import axios from 'axios'

const API_HOST = process.env.REACT_APP_API_BASE_URL || 'https://api-dev-clinico.bali-nesia.com'
const API_PATH = '/api/v1/treatments'
const API_BASE = `${API_HOST}${API_PATH}`

// Action Types
export const TREATMENT_ACTIONS = {
  // Fetch treatments
  FETCH_TREATMENTS_REQUEST: 'FETCH_TREATMENTS_REQUEST',
  FETCH_TREATMENTS_SUCCESS: 'FETCH_TREATMENTS_SUCCESS',
  FETCH_TREATMENTS_FAILURE: 'FETCH_TREATMENTS_FAILURE',
  
  // Fetch single treatment
  FETCH_TREATMENT_REQUEST: 'FETCH_TREATMENT_REQUEST',
  FETCH_TREATMENT_SUCCESS: 'FETCH_TREATMENT_SUCCESS',
  FETCH_TREATMENT_FAILURE: 'FETCH_TREATMENT_FAILURE',
  
  // Create treatment
  CREATE_TREATMENT_REQUEST: 'CREATE_TREATMENT_REQUEST',
  CREATE_TREATMENT_SUCCESS: 'CREATE_TREATMENT_SUCCESS',
  CREATE_TREATMENT_FAILURE: 'CREATE_TREATMENT_FAILURE',
  
  // Update treatment
  UPDATE_TREATMENT_REQUEST: 'UPDATE_TREATMENT_REQUEST',
  UPDATE_TREATMENT_SUCCESS: 'UPDATE_TREATMENT_SUCCESS',
  UPDATE_TREATMENT_FAILURE: 'UPDATE_TREATMENT_FAILURE',
  
  // Delete treatment
  DELETE_TREATMENT_REQUEST: 'DELETE_TREATMENT_REQUEST',
  DELETE_TREATMENT_SUCCESS: 'DELETE_TREATMENT_SUCCESS',
  DELETE_TREATMENT_FAILURE: 'DELETE_TREATMENT_FAILURE',
  
  // Clear errors
  CLEAR_TREATMENT_ERRORS: 'CLEAR_TREATMENT_ERRORS',
  
  // Clear success states
  CLEAR_TREATMENT_SUCCESS: 'CLEAR_TREATMENT_SUCCESS',
  
  // Reset state
  RESET_TREATMENT_STATE: 'RESET_TREATMENT_STATE'
}

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null
  const headers = { 'Content-Type': 'application/json' }
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }
  return headers
}

/** GET treatments grouped by cost_effect_type (bill / salary). Returns { bill: [], salary: [] }. */
export const getTreatmentsGroupedByCostEffect = async (facilityUUID, patientType) => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null
  if (!token) return { bill: [], salary: [] }
  const params = new URLSearchParams({ facility: facilityUUID, patientType: patientType || 'clinic' })
  const res = await axios.get(`${API_BASE}/grouped-by-cost-effect?${params.toString()}`, { headers: getAuthHeaders() })
  const data = res?.data?.data ?? res?.data ?? {}
  return { bill: Array.isArray(data.bill) ? data.bill : [], salary: Array.isArray(data.salary) ? data.salary : [] }
}

// Fetch treatments with pagination and search
export const fetchTreatments = (params = {}) => {
  return async (dispatch) => {
    dispatch({ type: TREATMENT_ACTIONS.FETCH_TREATMENTS_REQUEST })
    
    try {
      const { page = 1, limit = 10, search = '', facility } = params
      const offset = Math.max(0, (Number(page) - 1) * Number(limit))
      
      const queryParams = new URLSearchParams({
        limit: String(1000), // Get all data, we'll paginate on frontend
        offset: String(0),
        facility
      })
      
      // Pass facility UUID if available
      if (facility) {
        queryParams.set('facility', facility)
        // Also send alternative param name used by some endpoints
        queryParams.set('facilityUUID', facility)
        // Some APIs expect numeric/opaque facilityId; pass same token if available
        queryParams.set('facilityId', facility)
      }

      // Only add search parameter if search term is not empty
      if (search && search.trim().length > 0) {
        // Try multiple common search parameter names
        queryParams.set('search', search.trim())
        queryParams.set('q', search.trim())
        queryParams.set('keyword', search.trim())
        queryParams.set('filter', search.trim())        
      }
      
      const response = await axios.get(`${API_BASE}?${queryParams.toString()}`, {
        headers: getAuthHeaders()
      })
      
      const data = response.data
      console.log('Full API Response:', data)
      console.log('Query params sent:', queryParams.toString())
      
      // Try different possible response structures
      let treatments = []
      let total = 0

      // Normalize various possible response shapes
      const candidates = [
        data,
        data?.data,
        data?.data?.data,
        data?.data?.treatments,
        data?.treatments
      ]
      for (const c of candidates) {
        if (Array.isArray(c)) { treatments = c; break }
      }
      if (!Array.isArray(treatments)) {
        // If backend returned a single object, wrap it as an array
        const objCandidates = candidates.filter(c => c && typeof c === 'object' && !Array.isArray(c))
        const firstObj = objCandidates.find(o => ('uuid' in o) || ('id' in o) || ('name' in o))
        treatments = firstObj ? [firstObj] : []
      }

      total = (
        data?.total ??
        data?.data?.total ??
        data?.data?.totalItems ??
        data?.data?.count ??
        data?.count ??
        treatments.length
      )
      
      // Debug log to check search results
      console.log('=== SEARCH DEBUG ===')
      console.log('Search term:', search.trim())
      console.log('Limit requested:', limit)
      console.log('Page requested:', page)
      console.log('API URL:', `${API_BASE}?${queryParams.toString()}`)
      console.log('Raw response:', response.data)
      console.log('Parsed treatments:', treatments)
      console.log('Treatments count:', treatments.length)
      console.log('Total count:', total)
      console.log('Expected limit:', limit, 'Actual results:', treatments.length)
      console.log('===================')
      
      dispatch({
        type: TREATMENT_ACTIONS.FETCH_TREATMENTS_SUCCESS,
        payload: {
          treatments: Array.isArray(treatments) ? treatments : [],
          total: Number(total) || 0,
          page: Number(page),
          limit: Number(limit),
          search: search.trim()
        }
      })
    } catch (error) {
      dispatch({
        type: TREATMENT_ACTIONS.FETCH_TREATMENTS_FAILURE,
        payload: error.response?.data?.message || error.message || 'Failed to fetch treatments'
      })
    }
  }
}

// Fetch single treatment by ID
export const fetchTreatment = (id, options = {}) => {
  return async (dispatch) => {
    dispatch({ type: TREATMENT_ACTIONS.FETCH_TREATMENT_REQUEST })
    
    try {
      const queryParams = new URLSearchParams()
      if (options.facility) {
        queryParams.set('facility', options.facility)
        queryParams.set('facilityUUID', options.facility)
        queryParams.set('facilityId', options.facility)
      }
      const response = await axios.get(`${API_BASE}/${id}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`, {
        headers: getAuthHeaders()
      })
      
      const data = response.data.data || response.data
      
      dispatch({
        type: TREATMENT_ACTIONS.FETCH_TREATMENT_SUCCESS,
        payload: data
      })
    } catch (error) {
      dispatch({
        type: TREATMENT_ACTIONS.FETCH_TREATMENT_FAILURE,
        payload: error.response?.data?.message || error.message || 'Failed to fetch treatment'
      })
    }
  }
}

// Create new treatment — body matches Go CreateTreatmentRequest
export const createTreatment = (treatmentData, options = {}) => {
  return async (dispatch) => {
    dispatch({ type: TREATMENT_ACTIONS.CREATE_TREATMENT_REQUEST })
    
    try {
      const facility = (options && typeof options === 'object' && options.facility) ||
        (typeof options === 'string' ? options : null) ||
        treatmentData.facilityUUID ||
        treatmentData.facility ||
        treatmentData.facilityID

      // Build body to match CreateTreatmentRequest
      const name = (treatmentData.name ?? treatmentData.treatmentName ?? '').trim()
      const treatmentName = (treatmentData.treatmentName ?? name).trim()
      const unitPrice = Number(treatmentData.unitPrice)
      const sellingPrice = Number(treatmentData.sellingPrice)
      const notes = (treatmentData.notes ?? '').trim().slice(0, 500)
      const treatmentTypeId = (treatmentData.treatmentTypeId !== null && treatmentData.treatmentTypeId !== undefined && treatmentData.treatmentTypeId !== '') ? (Number(treatmentData.treatmentTypeId) || null) : null
      const costEffectType = (treatmentData.cost_effect_type ?? treatmentData.costEffectType ?? '').toString().trim() || null
      const bpjsTarifId = (treatmentData.bpjsTarifId !== null && treatmentData.bpjsTarifId !== undefined && treatmentData.bpjsTarifId !== '') ? (Number(treatmentData.bpjsTarifId) || null) : null
      const facilityUUID = (treatmentData.facilityUUID || facility || '').trim()
      const penunjangIdsRaw = treatmentData.penunjangIds ?? treatmentData.penunjang_ids
      let penunjangIds = []
      if (Array.isArray(penunjangIdsRaw) && penunjangIdsRaw.length > 0) {
        penunjangIds = [...new Set(penunjangIdsRaw.map((x) => Number(x)).filter((n) => Number.isFinite(n)))]
      } else {
        const single = treatmentData.penunjangId ?? treatmentData.penunjang_id
        if (single !== null && single !== undefined && single !== '') {
          const n = Number(single)
          if (Number.isFinite(n)) penunjangIds = [n]
        }
      }

      let tarifs = []
      if (Array.isArray(treatmentData.tarifs)) {
        tarifs = treatmentData.tarifs.map((t) => ({ patientTypeId: Number(t.patientTypeId), price: Number(t.price) }))
      }

      let components = []
      if (Array.isArray(treatmentData.components)) {
        components = treatmentData.components.map((c) => {
          const out = { name: (c.name ?? '').trim(), amount: Number(c.amount), componentType: (c.componentType ?? '').trim() || null, description: (c.description ?? '').trim() || '' }
          if (c.medicineId !== null && c.medicineId !== undefined && c.medicineId !== '') {
            out.medicineId = Number(c.medicineId) || c.medicineId
          }
          if (c.roleId !== null && c.roleId !== undefined && c.roleId !== '') {
            out.roleId = Number(c.roleId) || c.roleId
          }
          return out
        })
      }

      let references = []
      if (Array.isArray(treatmentData.references)) {
        references = treatmentData.references.map((r) => ({ category: (r.category ?? '').trim(), reference: Number(r.reference) || r.reference, notes: (r.notes ?? '').trim() || '' }))
      }

      const payload = {
        name: treatmentName || name,
        treatmentName: treatmentName || name,
        unitPrice: Number.isFinite(unitPrice) ? unitPrice : 0,
        sellingPrice: Number.isFinite(sellingPrice) ? sellingPrice : 0,
        notes,
        treatmentTypeId,
        cost_effect_type: costEffectType,
        bpjsTarifId,
        penunjangIds,
        facilityUUID,
        tarifs,
        components,
        references
      }

      const queryParams = new URLSearchParams()
      if (facility) {
        queryParams.set('facility', facility)
        queryParams.set('facilityUUID', facility)
        queryParams.set('facilityId', facility)
      }

      const response = await axios.post(`${API_BASE}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`, payload, {
        headers: getAuthHeaders()
      })
      
      const data = response.data.data || response.data
      
      dispatch({
        type: TREATMENT_ACTIONS.CREATE_TREATMENT_SUCCESS,
        payload: data
      })
    } catch (error) {
      dispatch({
        type: TREATMENT_ACTIONS.CREATE_TREATMENT_FAILURE,
        payload: error.response?.data?.message || error.message || 'Failed to create treatment'
      })
    }
  }
}

// Update existing treatment — same body shape as create (CreateTreatmentRequest)
export const updateTreatment = (id, treatmentData, options = {}) => {
  return async (dispatch) => {
    dispatch({ type: TREATMENT_ACTIONS.UPDATE_TREATMENT_REQUEST })
    
    try {
      const facility = (options && typeof options === 'object' && options.facility) ||
        (typeof options === 'string' ? options : null) ||
        treatmentData.facilityUUID ||
        treatmentData.facility ||
        treatmentData.facilityID

      const name = (treatmentData.name ?? treatmentData.treatmentName ?? '').trim()
      const treatmentName = (treatmentData.treatmentName ?? name).trim()
      const unitPrice = Number(treatmentData.unitPrice)
      const sellingPrice = Number(treatmentData.sellingPrice)
      const notes = (treatmentData.notes ?? '').trim().slice(0, 500)
      const treatmentTypeId = (treatmentData.treatmentTypeId !== null && treatmentData.treatmentTypeId !== undefined && treatmentData.treatmentTypeId !== '') ? (Number(treatmentData.treatmentTypeId) || null) : null
      const costEffectType = (treatmentData.cost_effect_type ?? treatmentData.costEffectType ?? '').toString().trim() || null
      const bpjsTarifId = (treatmentData.bpjsTarifId !== null && treatmentData.bpjsTarifId !== undefined && treatmentData.bpjsTarifId !== '') ? (Number(treatmentData.bpjsTarifId) || null) : null
      const facilityUUID = (treatmentData.facilityUUID || facility || '').trim()
      const penunjangIdsRawUpdate = treatmentData.penunjangIds ?? treatmentData.penunjang_ids
      let penunjangIds = []
      if (Array.isArray(penunjangIdsRawUpdate) && penunjangIdsRawUpdate.length > 0) {
        penunjangIds = [...new Set(penunjangIdsRawUpdate.map((x) => Number(x)).filter((n) => Number.isFinite(n)))]
      } else {
        const single = treatmentData.penunjangId ?? treatmentData.penunjang_id
        if (single !== null && single !== undefined && single !== '') {
          const n = Number(single)
          if (Number.isFinite(n)) penunjangIds = [n]
        }
      }

      let tarifs = []
      if (Array.isArray(treatmentData.tarifs)) {
        tarifs = treatmentData.tarifs.map((t) => ({ patientTypeId: Number(t.patientTypeId), price: Number(t.price) }))
      }

      let components = []
      if (Array.isArray(treatmentData.components)) {
        components = treatmentData.components.map((c) => {
          const out = { name: (c.name ?? '').trim(), amount: Number(c.amount), componentType: (c.componentType ?? '').trim() || null, description: (c.description ?? '').trim() || '' }
          if (c.medicineId !== null && c.medicineId !== undefined && c.medicineId !== '') {
            out.medicineId = Number(c.medicineId) || c.medicineId
          }
          if (c.roleId !== null && c.roleId !== undefined && c.roleId !== '') {
            out.roleId = Number(c.roleId) || c.roleId
          }
          return out
        })
      }

      let references = []
      if (Array.isArray(treatmentData.references)) {
        references = treatmentData.references.map((r) => ({ category: (r.category ?? '').trim(), reference: Number(r.reference) || r.reference, notes: (r.notes ?? '').trim() || '' }))
      }

      const payload = {
        name: treatmentName || name,
        treatmentName: treatmentName || name,
        unitPrice: Number.isFinite(unitPrice) ? unitPrice : 0,
        sellingPrice: Number.isFinite(sellingPrice) ? sellingPrice : 0,
        notes,
        treatmentTypeId,
        cost_effect_type: costEffectType,
        bpjsTarifId,
        penunjangIds,
        facilityUUID,
        tarifs,
        components,
        references
      }

      const queryParams = new URLSearchParams()
      if (facility) {
        queryParams.set('facility', facility)
        queryParams.set('facilityUUID', facility)
        queryParams.set('facilityId', facility)
      }

      const response = await axios.put(`${API_BASE}/${id}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`, payload, {
        headers: getAuthHeaders()
      })
      
      const data = response.data.data || response.data
      
      dispatch({
        type: TREATMENT_ACTIONS.UPDATE_TREATMENT_SUCCESS,
        payload: data
      })
    } catch (error) {
      dispatch({
        type: TREATMENT_ACTIONS.UPDATE_TREATMENT_FAILURE,
        payload: error.response?.data?.message || error.message || 'Failed to update treatment'
      })
    }
  }
}

// Delete treatment
export const deleteTreatment = (id, options = {}) => {
  return async (dispatch) => {
    dispatch({ type: TREATMENT_ACTIONS.DELETE_TREATMENT_REQUEST })
    
    try {
      const queryParams = new URLSearchParams()
      if (options.facility) {
        queryParams.set('facility', options.facility)
        queryParams.set('facilityUUID', options.facility)
        queryParams.set('facilityId', options.facility)
      }

      await axios.delete(`${API_BASE}/${id}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`, {
        headers: getAuthHeaders()
      })
      
      dispatch({
        type: TREATMENT_ACTIONS.DELETE_TREATMENT_SUCCESS,
        payload: id
      })
    } catch (error) {
      dispatch({
        type: TREATMENT_ACTIONS.DELETE_TREATMENT_FAILURE,
        payload: error.response?.data?.message || error.message || 'Failed to delete treatment'
      })
    }
  }
}

// Clear errors
export const clearTreatmentErrors = () => {
  return {
    type: TREATMENT_ACTIONS.CLEAR_TREATMENT_ERRORS
  }
}

// Clear success states
export const clearTreatmentSuccess = () => {
  return {
    type: TREATMENT_ACTIONS.CLEAR_TREATMENT_SUCCESS
  }
}

// Reset state
export const resetTreatmentState = () => {
  return {
    type: TREATMENT_ACTIONS.RESET_TREATMENT_STATE
  }
}

// ** Get table Data
export const getDataEncounterTreatments = ({ medicalRecordEncounterUUID, params }) => {        
    const accessToken = localStorage.getItem('accessToken')
    const authToken = `Bearer ${accessToken}`    

    return async dispatch => {
        await axios.get(`${process.env.REACT_APP_BE_BASE_URL}/api/v1/medical-records/${medicalRecordEncounterUUID}/treatments`, {
            params,
            headers: {
                Authorization: authToken
            }
        }).then(response => {            
            dispatch({
                type: 'GET_DATA_ENCOUNTER_TREATMENTS',
                allData: response.data.data.data,
                data: response.data.data.data,
                totalPages: response.data.data.totalPages,                
                params
            })
        })
    }
}

export const deleteDataTreatment = ({ medicalRecordEncounterUUID, uuid }) => {        
    const accessToken = localStorage.getItem('accessToken')
    const authToken = `Bearer ${accessToken}`    

    return async dispatch => {
        await axios.delete(`${process.env.REACT_APP_BE_BASE_URL}/api/v1/medical-records/${medicalRecordEncounterUUID}/treatments/${uuid}`,  {            
            headers: {
                Authorization: authToken
            }
        }).then(response => {             
            dispatch({
                type: 'SUCCESS_DELETE_ENCOUNTER_TREATMENT'
            })

            return response.data
        }).catch(error => {            
            dispatch({
                type: 'FAILED_DELETE_ENCOUNTER_TREATMENT'
            })
        })
    }
}

export const getTreatments = async params => {        
    const accessToken = localStorage.getItem('accessToken')
    const authToken = `Bearer ${accessToken}`    

    try {
        const response = await axios.get(`${process.env.REACT_APP_BE_BASE_URL}/api/v1/treatments`, {
            params,
            headers: { Authorization: authToken }
        })
      
        return  response.data.data
    } catch (err) {
        return null
    }
}

// Lightweight options loaders for async selects used in Treatment form
export const getTreatmentTypes = async params => {
  const accessToken = localStorage.getItem('accessToken')
  const authToken = `Bearer ${accessToken}`
  try {
    const response = await axios.get(`${process.env.REACT_APP_BE_BASE_URL}/api/v1/treatment-types`, {
      params,
      headers: { Authorization: authToken }
    })
    return response.data.data
  } catch (err) {
    return null
  }
}
